import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';
import { BasketActions, BasketActionTypes, BasketLoadingItem, LoadingItemTypes } from '../';
import { AppState, ErrorAction } from 'store';
import { api } from 'repositories/api';
import { ACTION_CREATOR_NAME, userHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { BasketItemModel, ProductTypes, BasketDetailsModel } from 'models/Basket';
import { removeSofaglideQueueSaga } from './removeSofaglide';
import { removeSofashieldQueueSaga } from './removeSofashield';
import { initBasketPageSaga } from './initBasketPage';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

export interface RemoveBasketItemAction extends Action<BasketActionTypes.REMOVE_BASKET_ITEM> {
    itemProductCode: string;
}

export interface RemoveBasketItemActionStart extends Action<BasketActionTypes.REMOVE_BASKET_ITEM_START> {
    loadingItem: BasketLoadingItem;
}

export interface RemoveBasketItemActionSuccess extends Action<BasketActionTypes.REMOVE_BASKET_ITEM_SUCCESS> {
    loadingItem: BasketLoadingItem;
}

export interface RemoveBasketItemActionError extends ErrorAction<BasketActionTypes.REMOVE_BASKET_ITEM_ERROR> {
    loadingItem: BasketLoadingItem;
}

export const removeBasketItem: (itemProductCode: string) => RemoveBasketItemAction =
    (itemProductCode: string) => ({
        type: BasketActionTypes.REMOVE_BASKET_ITEM,
        itemProductCode
    });
removeBasketItem[ACTION_CREATOR_NAME] = 'removeBasketItem';

export type RemoveBasketItemFunc = (...args: Parameters<typeof removeBasketItem>) => void;

/** The `removeBasketItem` saga was splitted into two part:
 * 1. Concurrent part.
 *    Allows to initiate several items removal processes at the same time (and display loaders for example);
 * 2. Sequential part.
 *    Places all calls to the queue since api cannot handle parallel calls for removal of basket items.
 *    Items can be removed only by `entryNumber` for now and it updates for each item after any removal from the items list.
 */
export function* removeBasketItemSaga({ itemProductCode }: RemoveBasketItemAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.REMOVE_BASKET_ITEM_START,
        loadingItem: { id: itemProductCode, loadingType: LoadingItemTypes.RemoveItem }
    });
}

export function* removeBasketItemQueueSaga({ loadingItem }: RemoveBasketItemActionStart): BasketSagaReturnType {
    try {
        let state: AppState = yield select();
        let entryItem = state.basketState.basket!.entries?.find((entry: BasketItemModel) => entry.product.code === loadingItem.id);
        const basketId = state.basketState.basket!.guid;
        let entryNumber = entryItem?.entryNumber;

        if (entryNumber !== undefined) {
            entryItem = entryItem!;
            if (entryItem.linkedSofaglide) {
                yield callBasketSaga(removeSofaglideQueueSaga, {
                    type: BasketActionTypes.REMOVE_SOFAGLIDE_START,
                    loadingItem: { id: entryItem.linkedSofaglide.baseProductCode, loadingType: LoadingItemTypes.RemoveSofaglide }
                });
            }

            if (entryItem.sofashieldQuantity && entryItem.sofashieldQuantity > 0) {
                yield callBasketSaga(removeSofashieldQueueSaga, {
                    type: BasketActionTypes.REMOVE_SOFASHIELD_START,
                    loadingItem: { id: entryItem.product.code, loadingType: LoadingItemTypes.RemoveSofashield }
                });
            }

            yield api.hybris.removeBasketItem(userHelper.getCurrentUserName(), basketId, entryNumber);

            yield callBasketSaga(initBasketPageSaga, {
                type: BasketActionTypes.INIT_BASKET_PAGE
            });

            state = yield select();

            sendAnalytics(entryItem, state.basketState.basket!);
        }

        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_BASKET_ITEM_SUCCESS,
            loadingItem
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_BASKET_ITEM_ERROR,
            loadingItem,
            error: errorToBasketErrorModel(error, 'removeBasketItemQueueSaga')
        });

        return false;
    }
}

function sendAnalytics(item: BasketItemModel, updatedBasket: BasketDetailsModel): void {
    analyticsHelper.withLogging(() => {
        const quantity = item.quantity;

        if (item.product.type === ProductTypes.DefaultProduct) {
            api.google.removeDefaultProduct(item, quantity);
        } else if (item.product.type === ProductTypes.FabricCareKit
            || item.product.type === ProductTypes.LeatherCareKit
            || item.product.type === ProductTypes.SofaRescue
            || item.product.type === ProductTypes.Donation
        ) {
            api.google.removeAdditionalProduct(item.product.type, item.product.code, quantity);
        }

        api.exponea.removeFromCart(updatedBasket, item, quantity);
    }, 'removeBasketItemQueueSaga');
}
